import { ThemeEvents } from '@theme/events';

const AUTO_DISMISS_MS = 3000;

/**
 * Toast after successful add-to-cart from product forms. Listens for cart:update from product-form-component.
 */
class AddToCartToast extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {number} */
  #hideTimer = 0;
  /** @type {number} */
  #teardownTimer = 0;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const closeBtn = this.querySelector('.add-to-cart-toast__close');
    closeBtn?.addEventListener('click', this.#dismiss, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
    this.#clearTimers();
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    const data = /** @type {CustomEvent} */ (event).detail?.data;
    if (data?.source !== 'product-form-component' || data?.didError) return;

    this.#show();
  };

  #show() {
    this.#clearTimers();

    this.hidden = false;
    this.classList.add('add-to-cart-toast--visible');

    const bar = this.querySelector('.add-to-cart-toast__progress-bar');
    if (bar instanceof HTMLElement) {
      bar.classList.remove('add-to-cart-toast__progress-bar--animating');
      void bar.offsetWidth;
      bar.classList.add('add-to-cart-toast__progress-bar--animating');
    }

    this.#hideTimer = window.setTimeout(this.#dismiss, AUTO_DISMISS_MS);
  }

  #dismiss = () => {
    this.#clearHideTimer();
    this.classList.remove('add-to-cart-toast--visible');

    if (this.#teardownTimer) clearTimeout(this.#teardownTimer);
    const duration = prefersReducedMotion() ? 0 : 200;
    this.#teardownTimer = window.setTimeout(() => {
      this.hidden = true;
      this.#teardownTimer = 0;
    }, duration);
  };

  #clearHideTimer() {
    if (this.#hideTimer) clearTimeout(this.#hideTimer);
    this.#hideTimer = 0;
  }

  #clearTimers() {
    this.#clearHideTimer();
    if (this.#teardownTimer) clearTimeout(this.#teardownTimer);
    this.#teardownTimer = 0;
  }
}

/** @returns {boolean} */
function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

if (!customElements.get('add-to-cart-toast')) {
  customElements.define('add-to-cart-toast', AddToCartToast);
}
